/**
 * Get translation string
 * @param {string} key - Translation key
 * @param {Object} params - Parameters to replace in translation
 * @returns {string} - Translated string
 */
const t = (key, params = {}) => {
  const translations = window.sharedCartTranslations || {};
  let translation = translations[key] || key;
  
  // Replace parameters in translation (e.g., {{ param }})
  // Handle both {{ param }} and {{param}} formats
  Object.keys(params).forEach(param => {
    const value = params[param];
    // Replace {{ param }} format
    translation = translation.replace(new RegExp(`{{\\s*${param}\\s*}}`, 'g'), value);
  });
  
  return translation;
};

const fetchWithTimeout = (url, opts = {}, ms = 8000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, { ...opts, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

/**
 * Show shared cart notification banner
 * @param {string} fullMessage - Full message text
 */
const showSharedCartNotification = (fullMessage) => {
  const banner = document.getElementById('shared-cart-notification-banner');
  const messageElement = document.getElementById('shared-cart-notification-message');
  const closeButton = document.getElementById('shared-cart-notification-close');
  
  // If banner doesn't exist (disabled for this market), silently return
  if (!banner || !messageElement) {
    return;
  }
  
  // Format message: first line bold, following lines normal.
  const messageLines = String(fullMessage || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  if (messageLines.length === 0) {
    return;
  }
  const [boldLine, ...detailLines] = messageLines;
  if (detailLines.length === 0) {
    messageElement.innerHTML = `<div class="shared-cart-bold-text"><strong>${boldLine}</strong></div>`;
  } else {
    const detailsHtml = detailLines
      .map((line) => `<div class="shared-cart-normal-text">${line}</div>`)
      .join('');
    messageElement.innerHTML = `
    <div class="shared-cart-bold-text"><strong>${boldLine}</strong></div>
    ${detailsHtml}`;
  }
  
  // Show banner
  banner.style.display = 'block';
  document.body.classList.add('shared-cart-banner-visible');
  
  // Scroll to top to show banner
  window.scrollTo({ top: 0, behavior: 'smooth' });
  
  // Close button handler
  if (closeButton) {
    const closeHandler = () => {
      banner.style.display = 'none';
      document.body.classList.remove('shared-cart-banner-visible');
      closeButton.removeEventListener('click', closeHandler);
    };
    closeButton.addEventListener('click', closeHandler);
  }

};

const showSharedCartTokenExpiredBanner = () => {
  const banner = document.getElementById('shared-cart-token-expired-banner');
  if (!banner) return;

  const titleElement = document.getElementById('shared-cart-token-expired-title');
  const bodyElement = document.getElementById('shared-cart-token-expired-body');
  const closeButton = document.getElementById('shared-cart-token-expired-close');
  const title = t('cartTokenExpiredTitle');
  const body = t('cartTokenExpiredBody');

  if (titleElement && title && title !== 'cartTokenExpiredTitle') {
    titleElement.textContent = title;
  }
  if (bodyElement && body && body !== 'cartTokenExpiredBody') {
    bodyElement.textContent = body;
  }

  banner.style.display = 'block';

  if (closeButton) {
    const closeHandler = () => {
      banner.style.display = 'none';
      closeButton.removeEventListener('click', closeHandler);
    };
    closeButton.addEventListener('click', closeHandler);
  }
};

const SHARED_CART_LOAD_ERROR = 'SHARED_CART_LOAD_ERROR';

const createSharedCartLoadError = (message) => {
  const error = new Error(message || 'Shared cart load failed');
  error.code = SHARED_CART_LOAD_ERROR;
  return error;
};

const isRecoverableCartAddFailure = (response) => (
  !!response && Number(response.status) === 422
);

const showSharedCartLoadErrorBanner = () => {
  const banner = document.getElementById('shared-cart-load-error-banner');
  if (!banner) return;

  const titleElement = document.getElementById('shared-cart-load-error-title');
  const bodyElement = document.getElementById('shared-cart-load-error-body');
  const reloadButton = document.getElementById('shared-cart-load-error-reload');
  const closeButton = document.getElementById('shared-cart-load-error-close');
  const title = t('cartLoadErrorTitle');
  const body = t('cartLoadErrorBody');
  const reloadLabel = t('cartLoadErrorReload');

  if (titleElement && title && title !== 'cartLoadErrorTitle') {
    titleElement.textContent = title;
  }
  if (bodyElement && body && body !== 'cartLoadErrorBody') {
    bodyElement.textContent = body;
  }
  if (reloadButton && reloadLabel && reloadLabel !== 'cartLoadErrorReload') {
    reloadButton.textContent = reloadLabel;
  }

  banner.style.display = 'block';

  if (reloadButton) {
    const reloadHandler = () => {
      window.location.reload();
    };
    reloadButton.addEventListener('click', reloadHandler, { once: true });
  }

  if (closeButton) {
    const closeHandler = () => {
      banner.style.display = 'none';
      closeButton.removeEventListener('click', closeHandler);
    };
    closeButton.addEventListener('click', closeHandler);
  }
};

const showSharedCartNotificationsInOrder = (messages) => {
  const queue = (messages || []).filter((msg) => String(msg || '').trim() !== '');
  if (queue.length === 0) return;
  queue.forEach((msg, index) => {
    setTimeout(() => {
      showSharedCartNotification(msg);
    }, index * 2500);
  });
};

const getSameRegionMessageKey = (baseKey, count) => {
  return count === 1 ? `${baseKey}Single` : `${baseKey}Multiple`;
};

const getUniqueNotificationTitles = (titles) => {
  return [...new Set((titles || []).map((title) => String(title || '').trim()).filter(Boolean))];
};

const getUniqueVariantIds = (variantIds) => {
  return [...new Set(
    (variantIds || [])
      .map((variantId) => parseInt(String(variantId || '').trim(), 10))
      .filter((variantId) => Number.isFinite(variantId))
  )];
};

const stripCartUpdatedPrefix = (message) => {
  const title = String(t('cartUpdated') || '').trim();
  const msg = String(message || '').trim();
  if (msg === '' || title === '') return msg;
  if (msg === title) return '';
  if (msg.startsWith(`${title} - `)) return msg.slice(title.length + 3).trim();
  if (msg.startsWith(`${title}. `)) return msg.slice(title.length + 2).trim();
  return msg;
};

const buildSharedCartBannerMessage = (detailMessages = []) => {
  const title = String(t('cartUpdated') || '').trim() || 'Your cart has been updated';
  const details = [...new Set(
    (detailMessages || [])
      .map((msg) => stripCartUpdatedPrefix(msg))
      .map((msg) => String(msg || '').trim())
      .filter(Boolean)
  )];
  return [title, ...details].join('\n');
};

const getAvailabilityMessage = (variantIds = []) => {
  const availabilityIssueVariantIds = getUniqueVariantIds(variantIds);
  if (availabilityIssueVariantIds.length > 0) {
    window.sharedCartAvailabilityIssueVariantIds = availabilityIssueVariantIds;
  }
  if (availabilityIssueVariantIds.length === 0) return '';
  return t('availabilityUpdatedGeneric');
};

const sharedCartSafetyTimer = setTimeout(() => {
  const loader = document.getElementById('page-loader');
  if (loader) loader.classList.add('hidden');
  const banner = document.querySelector('.invalid_url_sharecart');
  if (banner) banner.style.display = 'block';
}, 30000);

const loader2 = document.getElementById('page-loader');

const checkCartTokenInUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('cart_token');
};

const getStoredPartnerDetails = () => {
  try {
    const raw = localStorage.getItem('partnerInfo');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const formatSharedCartPartnerName = (details) => {
  if (!details) return '';
  const format = typeof capitalizeName === 'function'
    ? capitalizeName
    : (value) => String(value || '').trim();
  return [details.FirstName, details.LastName].filter(Boolean).map(format).join(' ').trim();
};

const getSharedCartPartnerInitials = (details) => {
  const first = String(details?.FirstName || '').trim();
  const last = String(details?.LastName || '').trim();
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
};

const waitForShopifyAccountPartnerResolved = () => {
  if (window.shopifyAccountPartnerResolved) return Promise.resolve();
  return new Promise((resolve) => {
    document.addEventListener('shopifyAccountPartnerResolved', resolve, { once: true });
  });
};

const waitForPartnerInitialized = () => {
  if (window.partnerInitialized) return Promise.resolve();
  return new Promise((resolve) => {
    document.addEventListener('partnerInitialized', resolve, { once: true });
  });
};

const pickSharedCartPartnerDetails = (partnerId, details) => {
  const id = String(partnerId || '').trim();
  if (!id || !details) return null;
  if (String(details.commSysPartnerID || '').trim() !== id) return null;
  if (!formatSharedCartPartnerName(details)) return null;
  return details;
};

const resolveSharedCartPartnerDetails = async (partnerId) => {
  const fromMemory = pickSharedCartPartnerDetails(partnerId, window.lastFetchedPartnerDetails);
  if (fromMemory) return fromMemory;

  const fromStorage = pickSharedCartPartnerDetails(partnerId, getStoredPartnerDetails());
  if (fromStorage) return fromStorage;

  if (!window.partnerInitialized) {
    await waitForPartnerInitialized();
    const afterMemory = pickSharedCartPartnerDetails(partnerId, window.lastFetchedPartnerDetails);
    if (afterMemory) return afterMemory;
    return pickSharedCartPartnerDetails(partnerId, getStoredPartnerDetails());
  }

  return null;
};

const escapeSharedCartPartnerHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const fillSharedCartPartnerByline = (el, name) => {
  if (!el) return;
  const template = el.dataset.template || '';
  const nameHtml = `<span class="shared-cart-partner-message__name">${escapeSharedCartPartnerHtml(name)}</span>`;
  el.innerHTML = template.replace(/\{\{\s*name\s*\}\}/g, nameHtml);
};

const showSharedCartPartnerMessage = (details) => {
  const root = document.getElementById('shared-cart-partner-message');
  if (!root) return;

  const name = formatSharedCartPartnerName(details);
  if (!name) return;

  const initialsEl = root.querySelector('[data-shared-cart-partner-initials]');
  if (initialsEl) initialsEl.textContent = getSharedCartPartnerInitials(details);
  fillSharedCartPartnerByline(root.querySelector('[data-shared-cart-partner-by]'), name);
  root.hidden = false;
};

const isLoggedInCustomerWithAttachedPartner = () => {
  if (!window.KROWN?.customerId) return false;
  return window.hasShopifyAccountAttachedPartner === true;
};

const getLiveCartItemCount = async () => {
  try {
    if (window.JPCartService) {
      const cart = await window.JPCartService.getCart();
      return Number(cart?.item_count || 0);
    }
    const response = await fetchWithTimeout('/cart.js');
    if (!response || !response.ok) return 0;
    const cart = await response.json();
    return Number(cart?.item_count || 0);
  } catch (e) {
    return 0;
  }
};

const showSharedCartPartnerMessageForId = async (partnerId) => {
  const root = document.getElementById('shared-cart-partner-message');
  if (!root) return;

  const itemCount = await getLiveCartItemCount();
  if (itemCount === 0) return;

  if (window.KROWN?.customerId) {
    await waitForShopifyAccountPartnerResolved();
    if (isLoggedInCustomerWithAttachedPartner()) return;
  }
  const details = await resolveSharedCartPartnerDetails(partnerId);
  showSharedCartPartnerMessage(details);
};

const getSharedCartSharePartnerId = (sharedCartData) => String(sharedCartData?.partner_id || '').trim();

const rememberSharedCartSharePartnerId = (partnerId) => {
  const id = String(partnerId || '').trim();
  if (!id) return;
  sessionStorage.setItem('sharedCartPartnerId', id);
};

const announceSharedCartSharePartnerId = (partnerId) => {
  const id = String(partnerId || '').trim();
  window.sharedCartSharePartnerId = id;
  rememberSharedCartSharePartnerId(id);
};

const getRememberedSharedCartSharePartnerId = () => {
  const stored = String(sessionStorage.getItem('sharedCartPartnerId') || '').trim();
  if (stored) return stored;
  return String(window.sharedCartSharePartnerId || '').trim();
};

const getCartMarketContext = () => {
  return window.cartMarketRedirectContext || {};
};

const normalizeMarketCode = (value) => {
  return String(value || '').trim().toUpperCase();
};

const normalizeMarketIconKey = (value) => {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace('flag-icon-', '')
    .replace(/[^a-z]/g, '');
};

const marketCodeFromIconKey = (iconKey) => {
  const key = normalizeMarketIconKey(iconKey);
  const map = {
    usa: 'US',
    unitedstates: 'US',
    unitedkingdom: 'GB',
    uk: 'GB',
    greatbritain: 'GB',
    newzealand: 'NZ',
    poland: 'PL',
    germany: 'DE',
    france: 'FR',
    belgium: 'BE',
    netherlands: 'NL',
    austria: 'AT',
    switzerland: 'CH',
    denmark: 'DK',
    sweden: 'SE',
    finland: 'FI',
    ireland: 'IE',
    italy: 'IT',
    spain: 'ES',
    portugal: 'PT',
    israel: 'IL',
    mexico: 'MX',
    canada: 'CA',
    australia: 'AU',
    uae: 'AE'
  };
  if (map[key]) return map[key];
  if (key.length === 2) return key.toUpperCase();
  return '';
};

const marketCodeFromHost = (href) => {
  if (!href) return '';
  try {
    const host = new URL(href).hostname;
    const subdomain = (host.split('.')[0] || '').trim();
    return normalizeMarketCode(subdomain);
  } catch (e) {
    return '';
  }
};

const parseMarketsList = () => {
  const raw = getCartMarketContext().marketsListRaw;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const resolveMarketMeta = (marketCode) => {
  const normalizedCode = normalizeMarketCode(marketCode);
  if (!normalizedCode) return null;
  const markets = parseMarketsList();
  for (const market of markets) {
    const codeFromIcon = marketCodeFromIconKey(market && market.icon);
    const codeFromHost = marketCodeFromHost(market && market.v_href);
    if (codeFromIcon === normalizedCode || codeFromHost === normalizedCode) {
      return {
        code: normalizedCode,
        label: (market && market.label) || normalizedCode,
        href: (market && market.v_href) || '',
        icon: normalizeMarketIconKey(market && market.icon)
      };
    }
  }
  return { code: normalizedCode, label: normalizedCode, href: '', icon: normalizedCode.toLowerCase() };
};

const buildHeaderLikeMarketUrl = (baseUrl) => {
  if (!baseUrl) return '';
  try {
    const url = new URL(baseUrl);
    const currentUrl = new URL(window.location.href);
    const path = currentUrl.pathname || '/';
    const search = currentUrl.search || '';

    const localePattern = /^[a-z]{2}(?:-[A-Z]{2})?$/;
    const baseLang = (url.pathname.split('/')[1] || '').trim();
    const currentLang = (path.split('/')[1] || '').trim();

    if (localePattern.test(baseLang)) {
      let cleanPath = path;
      if (localePattern.test(currentLang)) {
        const segments = path.split('/');
        segments.splice(1, 1);
        cleanPath = segments.join('/') || '/';
      }
      url.pathname = '/' + baseLang + cleanPath;
    } else {
      let cleanPath = path;
      if (localePattern.test(currentLang)) {
        const segments = path.split('/');
        segments.splice(1, 1);
        cleanPath = segments.join('/') || '/';
      }
      url.pathname = cleanPath;
    }

    if (search) {
      const searchParams = new URLSearchParams(search);
      for (const [key, value] of searchParams.entries()) {
        url.searchParams.set(key, value);
      }
    }

    url.searchParams.set('manualCountryChange', 'true');
    return url.toString();
  } catch (e) {
    return baseUrl;
  }
};

const updateMarketRedirectModalCta = (sourceMarketCode, destinationMarketCode) => {
  const modal = typeof window.getCartMarketRedirectModal === 'function'
    ? window.getCartMarketRedirectModal()
    : document.querySelector('.header-market-redirect-modal');
  if (!modal) return;

  const sourceMarket = resolveMarketMeta(sourceMarketCode);
  const destinationMarket = resolveMarketMeta(destinationMarketCode);
  const stayLabels = modal.querySelectorAll('.header-market-redirect-modal__stay .header-market-redirect-modal__btn-label');
  const continueCountry = modal.querySelector('.header-market-redirect-modal__continue-country');
  const bodyContainer = modal.querySelector('.header-market-redirect-modal__body');
  if (stayLabels.length > 1 && destinationMarket && destinationMarket.label) {
    stayLabels[1].textContent = destinationMarket.label;
  }
  const stayFlag = modal.querySelector('.header-market-redirect-modal__stay .header-market-redirect-modal__flag');
  if (stayFlag && destinationMarket && destinationMarket.icon) {
    stayFlag.className = 'header-market-redirect-modal__flag flag-icon flag-icon-' + destinationMarket.icon;
  }
  if (continueCountry && sourceMarket && sourceMarket.label) {
    continueCountry.textContent = sourceMarket.label;
  }
  const continueFlag = modal.querySelector('.header-market-redirect-modal__continue .header-market-redirect-modal__flag');
  if (continueFlag && sourceMarket && sourceMarket.icon) {
    continueFlag.className = 'header-market-redirect-modal__flag header-market-redirect-modal__flag--sm flag-icon flag-icon-' + sourceMarket.icon;
  }
  const continueLink = modal.querySelector('.header-market-redirect-modal__continue');
  if (continueLink && sourceMarket && sourceMarket.href) {
    continueLink.setAttribute('href', buildHeaderLikeMarketUrl(sourceMarket.href));
  }
  if (bodyContainer && sourceMarket && destinationMarket) {
    const existingNote = modal.querySelector('.header-market-redirect-modal__source-note');
    const noteText = 'This cart was generated in ' + sourceMarket.label + '.';
    if (existingNote) {
      existingNote.textContent = noteText;
      existingNote.hidden = true;
    } else {
      const note = document.createElement('p');
      note.className = 'header-market-redirect-modal__source-note';
      note.textContent = noteText;
      note.hidden = true;
      bodyContainer.appendChild(note);
    }
  }
};

const maybeShowCartSourceMarketRedirectModal = (sharedCartData) => {
  const getSourceMarketCodeForRedirect = () => {
    const useUrlRegionCompare = !!KROWN?.settings?.shared_cart?.include_market_in_url;
    const sourceFromMarketKey = normalizeMarketCode(getSharedCartSourceMarket(sharedCartData));
    const sourceFromUrlRegion = normalizeMarketCode(getSharedCartRegion(sharedCartData?.current_url));

    if (useUrlRegionCompare) {
      return sourceFromUrlRegion;
    }

    return sourceFromMarketKey || sourceFromUrlRegion;
  };

  const sourceMarketCode = getSourceMarketCodeForRedirect();
  const destinationMarketCode = normalizeMarketCode(getCartMarketContext().currentMarketCode);
  if (!sourceMarketCode || !destinationMarketCode) return;
  if (sourceMarketCode === destinationMarketCode) return;
  updateMarketRedirectModalCta(sourceMarketCode, destinationMarketCode);
  if (typeof window.openCartMarketRedirectModal === 'function') {
    window.openCartMarketRedirectModal();
  }
};

// Accepts an optional preAddPromise that, if provided, is awaited inside
// addProductsToCart immediately before the batched cart/add.js fires. Lets
// the caller run an independent cart-mutation (e.g. /cart/clear.js) in
// parallel with the API call + validation pass without race-condition risk.
const retrieveCart = async (preAddPromise) => {
  const cartToken = checkCartTokenInUrl();
  const apiUrl = KROWN.settings.shared_cart.get_cart_by_token + cartToken;
  let response = null;
  try {
    response = await fetchWithTimeout(apiUrl, { method: 'POST' });
  } catch (_error) {
    throw createSharedCartLoadError('Shared cart retrieve request failed');
  }
  if (response && response.status === 404) {
    const expiredError = new Error('Shared cart token expired');
    expiredError.code = 'CART_TOKEN_EXPIRED';
    throw expiredError;
  }
  if (!response || !response.ok) {
    throw createSharedCartLoadError('Failed to fetch shared cart by token');
  }
  const data = await response.json();
  await addProductsToCart(data, preAddPromise);
  return data;
};

/**
 * Get current store region from URL
 */
const getCurrentStoreRegion = () => {
  const currentUrl = window.location.href;
  try {
    const url = new URL(currentUrl);
    const hostname = url.hostname;
    // Extract region from hostname (e.g., us.mkt.juiceplus.com -> us)
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      const region = parts[0];
      return region; // First part is usually the region code
    }
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Extract region from shared cart URL
 */
const getSharedCartRegion = (sharedCartUrl) => {
  if (!sharedCartUrl) {
    return null;
  }
  try {
    const url = new URL(sharedCartUrl);
    const hostname = url.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      const region = parts[0];
      return region;
    }
    return null;
  } catch (e) {
    return null;
  }
};

const SHARED_CART_RESOLVE_BATCH_LIMIT = 50;

const joinProxyAndPath = (base, path) => {
  const b = (base || '').replace(/\/+$/, '');
  const p = (path || '').replace(/^\/+/, '');
  return `${b}/${p}`;
};

const parseShopifyGidToNumericId = (gid) => {
  if (gid == null) {
    return null;
  }
  if (typeof gid === 'number' && Number.isFinite(gid)) {
    return gid;
  }
  const s = String(gid);
  const parts = s.split('/');
  const last = parts[parts.length - 1];
  const n = parseInt(last, 10);
  return Number.isFinite(n) ? n : null;
};

const toCents = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.round(num) : null;
};

const getEffectiveLineFinalPrice = (lineItem) => {
  if (!lineItem || typeof lineItem !== 'object') return null;
  // For price-change comparison we must use the effective line amount after
  // discounts/promotions. In Shopify cart payloads, final_price/discounted_price
  // reflect that value, while selling_plan_allocation.price can remain the base
  // subscription price and cause false "price changed" notifications.
  const finalPriceCandidates = [
    lineItem.final_price,
    lineItem.discounted_price,
    lineItem?.selling_plan_allocation?.price,
    lineItem.price
  ];
  for (const candidate of finalPriceCandidates) {
    const cents = toCents(candidate);
    if (cents != null) return cents;
  }
  return null;
};

const getLineGlobalId = (lineItem) => {
  const properties = lineItem?.properties || {};
  return String(properties?._globalId || properties?._globalid || '').trim();
};

const findMatchingAddedCartLine = (cartItems, requestedItem) => {
  const items = Array.isArray(cartItems) ? cartItems : [];
  const requestedVariantId = parseInt(String(requestedItem?.id || ''), 10);
  if (!Number.isFinite(requestedVariantId)) return null;

  const requestedSellingPlanId = parseInt(String(requestedItem?.selling_plan || ''), 10);
  const requestedGlobalId = getLineGlobalId(requestedItem);

  return items.find((line) => {
    const lineVariantId = parseInt(String(line?.variant_id || ''), 10);
    if (lineVariantId !== requestedVariantId) return false;

    if (Number.isFinite(requestedSellingPlanId)) {
      const lineSellingPlanId = parseInt(String(line?.selling_plan_allocation?.selling_plan?.id || ''), 10);
      if (!Number.isFinite(lineSellingPlanId) || lineSellingPlanId !== requestedSellingPlanId) return false;
    }

    if (requestedGlobalId !== '') {
      const lineGlobalId = getLineGlobalId(line);
      if (lineGlobalId !== requestedGlobalId) return false;
    }

    return true;
  }) || null;
};

const getSharedCartSourceMarket = (sharedCartData) => {
  const raw = sharedCartData?.market ?? '';
  return String(raw).trim();
};

const getSharedCartMarketClassification = (sharedCartData) => {
  const classifyByUrlRegion = () => {
    const currentRegion = getCurrentStoreRegion();
    const sharedRegion = getSharedCartRegion(sharedCartData?.current_url);
    if (currentRegion && sharedRegion) {
      const isSameRegion = currentRegion.toLowerCase() === sharedRegion.toLowerCase();
      return {
        isSameRegion,
        isCrossMarket: !isSameRegion,
        strategy: 'url_region'
      };
    }
    return null;
  };

  const useUrlRegionCompare = !!KROWN?.settings?.shared_cart?.include_market_in_url;
  if (useUrlRegionCompare) {
    const urlClassification = classifyByUrlRegion();
    if (urlClassification) {
      return urlClassification;
    }
    return {
      isSameRegion: true,
      isCrossMarket: false,
      strategy: 'default_same_region_missing_url_region'
    };
  }

  const source = getSharedCartSourceMarket(sharedCartData);
  const dest = String(KROWN?.settings?.current_country_code || '').trim();
  if (source && dest) {
    const isSameRegion = source.toUpperCase() === dest.toUpperCase();
    return {
      isSameRegion,
      isCrossMarket: !isSameRegion,
      strategy: 'market_key'
    };
  }

  const urlFallbackClassification = classifyByUrlRegion();
  if (urlFallbackClassification) {
    return {
      ...urlFallbackClassification,
      strategy: 'url_region_fallback_missing_market_key'
    };
  }

  return {
    isSameRegion: true,
    isCrossMarket: false,
    strategy: 'default_same_region'
  };
};

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

// Market-agnostic products that have no per-market global_product.id and are
// resolved by a fixed SKU on the backend (mirrors the app's SKU_RESOLVED_HANDLES).
// These lines have no _globalId, but must still be sent to resolve-batch by handle.
const SKU_RESOLVED_HANDLE_PATTERNS = [/partner-application-fee/i];
const isSkuResolvableHandle = (handle) =>
  SKU_RESOLVED_HANDLE_PATTERNS.some((re) => re.test(String(handle || '')));

const isV2Storefront = () => KROWN?.settings?.shared_cart?.v2_enabled === true;

const fetchSharedCartBatchResolve = async (items, destinationMarket) => {
  const proxy = window.jpapp?.proxyUrl;
  const path = KROWN?.settings?.shared_cart?.resolve_batch_path || '/apps/otp/shared-cart/resolve-batch';
  const shop = KROWN?.settings?.shared_cart?.shop_permanent_domain;
  if (!proxy || !shop) {
    return { ok: false, status: 0, items: [] };
  }
  const payloadItems = Array.isArray(items)
    ? items.map((item) => ({
      global_id: String(item?.global_id || '').trim(),
      quantity: Number(item?.quantity) > 0 ? Number(item.quantity) : 1,
      handle: String(item?.handle || '').trim(),
      sourceMarket: String(item?.sourceMarket || '').trim()
    }))
    : [];
  const payload = {
    shop,
    destination_market: String(destinationMarket).trim().toUpperCase(),
    v2_enabled: isV2Storefront(),
    items: payloadItems
  };
  const url = path;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  }).catch(() => null);
  if (!res) {
    return { ok: false, status: 0, items: [] };
  }
  const data = await res.json().catch(() => ({}));
  return {
    ok: res.ok,
    status: res.status,
    items: Array.isArray(data?.items) ? data.items : []
  };
};

/**
 * Get current locale from URL path (prioritize URL over window.currentLocale)
 * @returns {string} - Current locale code from URL (e.g., 'en', 'de', 'es') or empty string
 */
const getCurrentLocale = () => {
  // Prioritize URL path over window.currentLocale to match actual URL structure
  // Extract locale from URL (e.g., /en/products/... or /de/products/...)
  const pathMatch = window.location.pathname.match(/^\/([a-z]{2}(?:-[A-Z]{2})?)\//);
  if (pathMatch) {
    return pathMatch[1];
  }
  
  // Fallback to window.currentLocale if no locale in URL path
  if (window.currentLocale) {
    return window.currentLocale;
  }
  
  // Return empty string if no locale found (will use /products/ without locale)
  return '';
};

const variantSellingPlanMetaCache = new Map();

const fetchVariantSellingPlansForProductHandle = async (productHandle) => {
  if (!productHandle) {
    return {};
  }
  if (variantSellingPlanMetaCache.has(productHandle)) {
    return variantSellingPlanMetaCache.get(productHandle);
  }
  try {
    const url = `/products/${encodeURIComponent(productHandle)}?view=variant-selling-plans`;
    const res = await fetchWithTimeout(url).catch(() => null);
    if (!res || !res.ok) {
      variantSellingPlanMetaCache.set(productHandle, {});
      return {};
    }
    const parsed = JSON.parse((await res.text()).trim());
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      variantSellingPlanMetaCache.set(productHandle, parsed);
      return parsed;
    }
  } catch (_e) {
    /* ignore */
  }
  variantSellingPlanMetaCache.set(productHandle, {});
  return {};
};

const getSellingPlanMetaFromMap = (map, variantId, sellingPlanId) => {
  if (!map || !variantId || !sellingPlanId) {
    return { name: '', description: '', price: null, compare_at_price: null };
  }
  const variantNode = map[String(variantId)];
  if (!variantNode || typeof variantNode !== 'object') {
    return { name: '', description: '', price: null, compare_at_price: null };
  }
  const plan = variantNode?.selling_plans?.[String(sellingPlanId)]
    || variantNode?.fallback_selling_plans?.[String(sellingPlanId)];
  if (!plan || typeof plan !== 'object') {
    return { name: '', description: '', price: null, compare_at_price: null };
  }
  return {
    name: String(plan.name || '').trim(),
    description: String(plan.description || '').trim(),
    price: Number.isFinite(Number(plan.price)) ? Number(plan.price) : null,
    compare_at_price: Number.isFinite(Number(plan.compare_at_price)) ? Number(plan.compare_at_price) : null
  };
};

const getVariantMetaFromMap = (map, variantId) => {
  if (!map || !variantId) {
    return null;
  }
  const node = map[String(variantId)];
  return node && typeof node === 'object' ? node : null;
};

const firstSellingPlanIdFrom = (plans) => {
  if (!plans || typeof plans !== 'object') {
    return null;
  }
  const ids = Object.keys(plans);
  if (ids.length === 0) {
    return null;
  }
  const parsed = parseInt(ids[0], 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveSellingPlanIdForVariant = (map, variantId, storedSellingPlanId) => {
  const node = getVariantMetaFromMap(map, variantId);
  if (!node) {
    return storedSellingPlanId;
  }
  const stored = storedSellingPlanId ? String(storedSellingPlanId) : '';
  if (stored !== '' && node.selling_plans && node.selling_plans[stored]) {
    return parseInt(stored, 10);
  }
  const active = firstSellingPlanIdFrom(node.selling_plans);
  if (active) {
    return active;
  }
  if (stored !== '' && node.fallback_selling_plans && node.fallback_selling_plans[stored]) {
    return parseInt(stored, 10);
  }
  return firstSellingPlanIdFrom(node.fallback_selling_plans);
};

/**
 * Fetch translated product title
 * @param {string} productHandle - The product handle
 * @param {number} variantId - The variant ID
 * @returns {Promise<string|null>} - Translated product title or null
 */
// Use Shopify.routes.root so the URL auto-includes the current market's
// locale prefix (e.g. '/en/', '/de/', or '/'). Avoids the manual locale +
// fallback-URL loop and the brittle getCurrentLocale() dependency.
//
// Cached at module scope by handle — product title is product-level (same
// across all variants), so repeat items in the same cart (or repeat carts in
// the same session) hit memory instead of the network. Locale doesn't change
// mid-session, so the cached value stays correct.
const translatedTitleCache = new Map();

const fetchTranslatedProductTitle = async (productHandle, variantId) => {
  if (!productHandle) return null;
  if (translatedTitleCache.has(productHandle)) {
    return translatedTitleCache.get(productHandle);
  }
  try {
    const root = (Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const productUrl = `${root}products/${productHandle}.js`;
    const response = await fetchWithTimeout(productUrl).catch(() => null);
    if (!response || !response.ok) {
      translatedTitleCache.set(productHandle, null);
      return null;
    }
    const productData = await response.json();
    const variant = productData.variants.find((v) => v.id === variantId);
    const title = variant && productData.title ? productData.title : null;
    translatedTitleCache.set(productHandle, title);
    return title;
  } catch (error) {
    // Silently fail — falls back to shared cart title at the call site.
    translatedTitleCache.set(productHandle, null);
    return null;
  }
};

/**
 * Fetch variant data from Shopify to check availability and price
 * @param {number} variantId - The variant ID
 * @param {string} productHandle - The product handle
 * @param {number|null} sellingPlanId - The selling plan ID (if subscription)
 */
const fetchVariantData = async (variantId, productHandle, sellingPlanId = null) => {
  try {
    const variantMetaMap = productHandle
      ? await fetchVariantSellingPlansForProductHandle(productHandle)
      : null;
    const variantMeta = getVariantMetaFromMap(variantMetaMap, variantId);
    const sellingPlanMeta = getSellingPlanMetaFromMap(variantMetaMap, variantId, sellingPlanId);
    if (variantMeta) {
      let price = Number.isFinite(Number(variantMeta.price)) ? Number(variantMeta.price) : 0;
      let compareAtPrice = Number.isFinite(Number(variantMeta.compare_at_price)) ? Number(variantMeta.compare_at_price) : null;
      if (sellingPlanId && sellingPlanMeta.price != null) {
        price = sellingPlanMeta.price;
      }
      if (sellingPlanId && sellingPlanMeta.compare_at_price != null) {
        compareAtPrice = sellingPlanMeta.compare_at_price;
      }

      return {
        available: !!variantMeta.available,
        price,
        final_price: price,
        compare_at_price: compareAtPrice,
        inventory_quantity: Number.isFinite(Number(variantMeta.inventory_quantity)) ? Number(variantMeta.inventory_quantity) : 0,
        inventory_policy: String(variantMeta.inventory_policy || ''),
        selling_plan_description: sellingPlanMeta.description,
        selling_plan_name: sellingPlanMeta.name,
        discount_value: String(variantMeta.discount_value || '').trim(),
        monthly_base_total: String(variantMeta.monthly_base_total || '').trim()
      };
    }
    
    // Fallback: try to get from cart.js to see current prices
    const cartResponse = await fetchWithTimeout('/cart.js').catch(() => null);
    if (cartResponse && cartResponse.ok) {
      const cartData = await cartResponse.json();
      const existingItem = cartData.items.find(item => 
        item.variant_id === variantId && 
        (!sellingPlanId || (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id === sellingPlanId))
      );
      if (existingItem) {
        // Use selling plan price if available, otherwise use regular price
        const itemPrice = existingItem.selling_plan_allocation && existingItem.selling_plan_allocation.price 
          ? existingItem.selling_plan_allocation.price 
          : existingItem.price;
        
        return {
          available: true,
          price: itemPrice,
          final_price: itemPrice,
          compare_at_price: existingItem.compare_at_price,
          selling_plan_description: String(existingItem?.selling_plan_allocation?.selling_plan?.description || '').trim(),
          selling_plan_name: String(existingItem?.selling_plan_allocation?.selling_plan?.name || '').trim(),
          discount_value: '',
          monthly_base_total: ''
        };
      }
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Shared cart helpers for strict property reconstruction.
 */
const getItemProperties = (item) => (
  item && item.properties && typeof item.properties === 'object' && !Array.isArray(item.properties)
    ? { ...item.properties }
    : {}
);

const parseVariantIdList = (raw) => String(raw || '')
  .split(',')
  .map((v) => parseInt(String(v).trim(), 10))
  .filter((v) => Number.isFinite(v));

const stringifyVariantIdList = (arr) => arr.filter((v) => Number.isFinite(v)).join(',');

const lineHasSourceSellingPlan = (item, props) => {
  const allocationPlanId = item?.selling_plan_allocation?.selling_plan?.id;
  if (allocationPlanId != null) {
    return true;
  }
  const sourceSellingPlanId = item?.selling_plan_id;
  if (sourceSellingPlanId != null && String(sourceSellingPlanId).trim() !== '') {
    return true;
  }
  return String(props?._sellingplan || '').trim() !== '';
};

const isSubscriptionProductLine = (line) => (
  (line?.properties &&
    line.properties._subscriptionType &&
    line.properties._subscriptionType !== '') ||
  (line?.properties &&
    line.properties._sellingplan &&
    line.properties._sellingplan !== '') ||
  !!line?.selling_plan
);

const isInstallmentSubscriptionLine = (line) => {
  if (!isSubscriptionProductLine(line)) {
    return false;
  }
  const subscriptionType = String(line?.properties?._subscriptionType || '').trim().toLowerCase();
  return subscriptionType.includes('installment');
};

// Shared carts store line properties as strings, so _hsf comes back as "true", not true.
const isHsfFlagTrue = (value) => value === true || String(value || '').trim().toLowerCase() === 'true';

const isHealthyStartLine = (line) => {
  const props = line?.properties || {};
  const isHsfFlag = isHsfFlagTrue(props._hsf);
  const hasAdultLinks = String(props._adultProductIds || '').trim() !== '';
  const hasChildLinks = String(props._childProductIds || '').trim() !== '';
  return isHsfFlag || hasAdultLinks || hasChildLinks;
};

const getInstallmentLocalConfig = () => {
  const variantId = parseInt(localStorage.getItem('installmentFeeVariantId') || '', 10);
  const sellingPlanId = parseInt(localStorage.getItem('installmentFeeVariantSellingPlanId') || '', 10);
  const globalId = String(localStorage.getItem('installmentFeeGlobalId') || '').trim();
  const monthlyBaseTotal = String(localStorage.getItem('installmentMetafieldValue') || '').trim();
  return {
    variantId: Number.isFinite(variantId) ? variantId : null,
    sellingPlanId: Number.isFinite(sellingPlanId) ? sellingPlanId : null,
    globalId,
    monthlyBaseTotal
  };
};

const isSourceInstallmentFeeLine = (item, sourceProps) => {
  const sourceGlobalId = String(sourceProps?._globalId ?? sourceProps?._globalid ?? '').trim();
  const installmentFeeGlobalId = String(localStorage.getItem('installmentFeeGlobalId') || '').trim();
  if (sourceGlobalId !== '' && installmentFeeGlobalId !== '' && sourceGlobalId === installmentFeeGlobalId) {
    return true;
  }
  // When destination market has no installment fee (installmentFeeGlobalId empty in localStorage),
  // fall back to pattern match so source fee lines are silently skipped instead of triggering the
  // "not available in your location" modal via a resolve-batch NOT_FOUND error.
  return /^FEE-INST/i.test(sourceGlobalId);
};

const isInstallmentFeeLine = (destinationVariantId) => {
  const installmentFeeVariantId = parseInt(localStorage.getItem('installmentFeeVariantId') || '', 10);
  return Number.isFinite(installmentFeeVariantId) && installmentFeeVariantId === destinationVariantId;
};

const detectLineType = (sourceProps, destinationVariantId) => {
  if (isInstallmentFeeLine(destinationVariantId)) return 'installment_fee';
  if (String(sourceProps._partner_fee || '') === 'true') return 'partner_fee';
  if (String(sourceProps._partner_fee_welcome_pack || '') === 'true' && String(sourceProps._partner_fee_bundle || '').trim() !== '') return 'partner_welcome_pack';
  if (String(sourceProps._partner_fee_bundle || '').trim() !== '') return 'partner_starter_pack';
  if (isHsfFlagTrue(sourceProps._hsf) && String(sourceProps._childProductIds || '').trim() !== '') return 'hsf_adult';
  if (isHsfFlagTrue(sourceProps._hsf) && String(sourceProps._adultProductIds || '').trim() !== '') return 'hsf_child';
  return 'normal';
};

const computeDiscountValue = (variantData) => {
  if (String(variantData?.discount_value || '').trim() !== '') {
    return String(variantData.discount_value).trim();
  }
  const price = Number(variantData?.final_price);
  const compare = Number(variantData?.compare_at_price);
  if (!Number.isFinite(price) || !Number.isFinite(compare) || compare <= price || compare <= 0) {
    return '';
  }
  const pct = ((compare - price) / compare) * 100;
  return pct.toFixed(1);
};

const recomputeOtsInstallment = (sourceValue, sourceMarket, destinationMarket) => {
  const src = String(sourceMarket || '').trim().toUpperCase();
  const dst = String(destinationMarket || '').trim().toUpperCase();
  if (src === 'IL' && dst === 'IL') {
    return String(sourceValue || '').trim() === 'true' ? 'true' : 'false';
  }
  return 'false';
};

const isManualPaymentMethod = (paymentMethod) => {
  const manualPaymentTypes = KROWN?.settings?.manualPaymentTypes;
  if (!Array.isArray(manualPaymentTypes)) {
    return false;
  }
  return manualPaymentTypes.includes(String(paymentMethod || '').trim());
};

const formatMonthlyBaseTotalForCart = (raw) => {
  const value = String(raw ?? '').trim();
  if (value === '') return '';
  if (/^\d+\.?\d*$/.test(value)) {
    const num = Number(value);
    if (!Number.isNaN(num)) {
      return num.toFixed(2);
    }
  }
  return value;
};

const buildReconstructedProperties = ({
  row,
  sourceProps,
  lineType,
  mappedPartnerFeeVariantId,
  mappedAdultVariantIds,
  mappedChildVariantIds,
  sourceMarket,
  destinationMarket,
  isCrossMarket,
  isManualPayment
}) => {
  const props = {};
  props._globalId = String(row.globalId || '').trim();
  if (lineType !== 'installment_fee') {
    props._subscriptionType = row.sellingPlanDescription ? row.sellingPlanDescription : '';
  }

  const discountValue = computeDiscountValue(row.variantData);
  const monthlyBaseTotal = formatMonthlyBaseTotalForCart(row.variantData?.monthly_base_total);
  const otsInstallment = recomputeOtsInstallment(sourceProps._ots_installment, sourceMarket, destinationMarket);

  const sellingPlanNameForCart = otsInstallment === 'true'
    ? String(KROWN?.settings?.ots?.ots_selling_plan_name || '')
    : String(row.sellingPlanName || '');
  if (row.sellingPlanId && isManualPayment) {
    props._sellingplan = String(row.sellingPlanId);
    if (isV2Storefront()) {
      props._sub_engine_selling_plan = String(row.sellingPlanId);
    }
    props._selling_plan_name = sellingPlanNameForCart;
  } else if (lineType !== 'installment_fee') {
    props._sellingplan = '';
    props._selling_plan_name = sellingPlanNameForCart;
  }

  switch (lineType) {
    case 'partner_fee':
      props._partner_fee = 'true';
      props._discountvalue = discountValue;
      break;
    case 'partner_welcome_pack':
      props._partner_fee_welcome_pack = 'true';
      props._partner_fee_bundle = String(mappedPartnerFeeVariantId);
      props._discountvalue = discountValue;
      break;
    case 'partner_starter_pack':
      props._partner_fee_bundle = String(mappedPartnerFeeVariantId);
      props._discountvalue = discountValue;
      break;
    case 'hsf_adult':
      props._hsf = true;
      props._discountvalue = discountValue;
      if (monthlyBaseTotal !== '') {
        props._monthly_base_total = monthlyBaseTotal;
      }
      if (isCrossMarket) {
        props._childProductIds = stringifyVariantIdList(mappedChildVariantIds);
      } else if (String(sourceProps._childProductIds || '').trim() !== '') {
        props._childProductIds = String(sourceProps._childProductIds).trim();
      }
      break;
    case 'hsf_child':
      props._hsf = true;
      props._discountvalue = discountValue;
      if (monthlyBaseTotal !== '') {
        props._monthly_base_total = monthlyBaseTotal;
      }
      if (isCrossMarket) {
        props._adultProductIds = stringifyVariantIdList(mappedAdultVariantIds);
      } else if (String(sourceProps._adultProductIds || '').trim() !== '') {
        props._adultProductIds = String(sourceProps._adultProductIds).trim();
      }
      break;
    case 'installment_fee':
      if (String(row.installmentMonthlyBaseTotal).trim() !== '') {
        props._monthly_base_total = String(row.installmentMonthlyBaseTotal).trim();
      }
      break;
    default:
      props._discountvalue = discountValue;
      if (monthlyBaseTotal !== '') {
        props._monthly_base_total = monthlyBaseTotal;
      }
      props._ots_installment = otsInstallment;
      break;
  }

  return props;
};

const demoteOrphanedHsfAdults = (validatedItems, validatedMeta, {
  sourceMarket,
  destinationMarket,
  isCrossMarket,
  isManualPayment
}) => {
  const importedChildSourceIds = new Set();
  let hasImportedHsfChild = false;

  for (const meta of validatedMeta) {
    if (meta.lineType !== 'hsf_child') continue;
    hasImportedHsfChild = true;
    importedChildSourceIds.add(meta.sourceVariantId);
  }

  for (let i = 0; i < validatedMeta.length; i++) {
    const meta = validatedMeta[i];
    if (meta.lineType !== 'hsf_adult') continue;

    const linkedChildIds = parseVariantIdList(meta.sourceProps._childProductIds);
    if (!linkedChildIds.length || linkedChildIds.some((id) => importedChildSourceIds.has(id))) {
      continue;
    }

    validatedItems[i].properties = buildReconstructedProperties({
      row: meta.row,
      sourceProps: meta.sourceProps,
      lineType: 'normal',
      mappedPartnerFeeVariantId: null,
      mappedAdultVariantIds: [],
      mappedChildVariantIds: [],
      sourceMarket,
      destinationMarket,
      isCrossMarket,
      isManualPayment
    });
  }

  return hasImportedHsfChild;
};

const sanitizeSharedCartHsfAttributes = (attributes, hasImportedHsfChild) => {
  if (hasImportedHsfChild || !attributes?.hsfChildDetails) {
    return attributes;
  }
  return { ...attributes, hsfChildDetails: null };
};

const finalizeResolvedRows = ({
  resolvedRows,
  errors,
  silentSkips = 0,
  isSameRegion,
  isCrossMarket,
  sourceMarket,
  destinationMarket,
  isManualPayment
}) => {
  const warnings = [];
  const validatedItems = [];
  const validatedMeta = [];
  const lineTitles = [];

  const sourceToDestVariantMap = new Map();
  resolvedRows.forEach((row) => {
    sourceToDestVariantMap.set(row.sourceVariantId, row.destinationVariantId);
  });

  const preliminaryValidRows = [];
  const skippedKeys = new Set();

  for (const row of resolvedRows) {
    const sourceProps = getItemProperties(row.sourceItem);
    const lineType = row.sourceLineType || detectLineType(sourceProps, row.destinationVariantId);
    const sourceHasSellingPlan = lineHasSourceSellingPlan(row.sourceItem, sourceProps);
    // const isUnavailable = !!(row.variantData && row.variantData.available === false);
    const isUnavailable = !row.variantData || row.variantData.available === false;
    const requiresSellingPlan = sourceHasSellingPlan || lineType === 'installment_fee';
    if (isUnavailable || (requiresSellingPlan && !row.sellingPlanId)) {
      errors.push({
        type: 'skipped_rule',
        variantId: row.sourceVariantId,
        title: row.title
      });
      skippedKeys.add(`${row.sourceVariantId}|${row.destinationVariantId}`);
      continue;
    }
    preliminaryValidRows.push({
      ...row,
      sourceProps,
      lineType,
      sourceHasSellingPlan
    });
  }

  const partnerFeeRows = preliminaryValidRows.filter((row) => row.lineType === 'partner_fee');
  const partnerFeeMap = new Map();
  partnerFeeRows.forEach((row) => {
    partnerFeeMap.set(row.sourceVariantId, row.destinationVariantId);
  });

  const hsfAdultRows = preliminaryValidRows.filter((row) => row.lineType === 'hsf_adult');
  const adultMap = new Map();
  hsfAdultRows.forEach((row) => {
    adultMap.set(row.sourceVariantId, row.destinationVariantId);
  });

  for (const row of preliminaryValidRows) {
    const key = `${row.sourceVariantId}|${row.destinationVariantId}`;
    if (skippedKeys.has(key)) continue;

    let mappedPartnerFeeVariantId = null;
    if (row.lineType === 'partner_welcome_pack' || row.lineType === 'partner_starter_pack') {
      const sourceBundleVariantId = parseInt(String(row.sourceProps._partner_fee_bundle || '').trim(), 10);
      if (Number.isFinite(sourceBundleVariantId) && partnerFeeMap.has(sourceBundleVariantId)) {
        mappedPartnerFeeVariantId = partnerFeeMap.get(sourceBundleVariantId);
      } else if (partnerFeeRows.length === 1) {
        mappedPartnerFeeVariantId = partnerFeeRows[0].destinationVariantId;
      }
      if (!Number.isFinite(mappedPartnerFeeVariantId)) {
        errors.push({ type: 'skipped_rule', variantId: row.sourceVariantId, title: row.title });
        skippedKeys.add(key);
        continue;
      }
    }

    let mappedAdultVariantIds = [];
    if (row.lineType === 'hsf_child') {
      const sourceAdultIds = parseVariantIdList(row.sourceProps._adultProductIds);
      mappedAdultVariantIds = sourceAdultIds
        .map((id) => adultMap.get(id))
        .filter((id) => Number.isFinite(id));
      if (mappedAdultVariantIds.length === 0) {
        errors.push({ type: 'skipped_rule', variantId: row.sourceVariantId, title: row.title });
        skippedKeys.add(key);
        continue;
      }
    }

    const mappedChildVariantIds = row.lineType === 'hsf_adult'
      ? parseVariantIdList(row.sourceProps._childProductIds)
        .map((id) => sourceToDestVariantMap.get(id))
        .filter((id) => Number.isFinite(id))
      : [];

    const reconstructedProperties = buildReconstructedProperties({
      row,
      sourceProps: row.sourceProps,
      lineType: row.lineType,
      mappedPartnerFeeVariantId,
      mappedAdultVariantIds,
      mappedChildVariantIds,
      sourceMarket,
      destinationMarket,
      isCrossMarket,
      isManualPayment
    });

    const sharedPriceRaw = row.sourceItem?.selling_plan_allocation?.price
      ? row.sourceItem.selling_plan_allocation.price
      : (row.sourceItem?.final_price || row.sourceItem?.price);
    const sharedPrice = toCents(sharedPriceRaw);
    const currentPrice = toCents(row.variantData?.final_price);
    if (
      isSameRegion &&
      row.lineType !== 'hsf_child' &&
      sharedPrice != null &&
      currentPrice != null &&
      currentPrice !== sharedPrice
    ) {
      warnings.push({
        variantId: row.destinationVariantId,
        title: row.title,
        sharedPrice,
        currentPrice
      });
    }

    const cartLine = {
      id: row.destinationVariantId,
      quantity: row.quantity,
      properties: reconstructedProperties
    };
    if (row.sellingPlanId && !isManualPayment) {
      cartLine.selling_plan = row.sellingPlanId;
    }
    validatedItems.push(cartLine);
    validatedMeta.push({
      lineType: row.lineType,
      sourceVariantId: row.sourceVariantId,
      sourceProps: row.sourceProps,
      row
    });
    lineTitles.push(row.title);
  }

  const hasImportedHsfChild = demoteOrphanedHsfAdults(validatedItems, validatedMeta, {
    sourceMarket,
    destinationMarket,
    isCrossMarket,
    isManualPayment
  });

  // Inject installment fee once when payload has installment subscription lines but no fee line.
  const installmentConfig = getInstallmentLocalConfig();
  const installmentSubscriptionLines = validatedItems.filter((line) => (
    isInstallmentSubscriptionLine(line) &&
    !isInstallmentFeeLine(line.id) &&
    !isHealthyStartLine(line)
  ));
  const hasInstallmentSubscription = installmentSubscriptionLines.length > 0;
  const installmentFeeIndexes = [];
  for (let i = 0; i < validatedItems.length; i++) {
    if (isInstallmentFeeLine(validatedItems[i]?.id)) {
      installmentFeeIndexes.push(i);
    }
  }

  // Remove dangling installment fee lines when no eligible installment products remain.
  if (!hasInstallmentSubscription && installmentFeeIndexes.length > 0) {
    for (let i = installmentFeeIndexes.length - 1; i >= 0; i--) {
      const feeIndex = installmentFeeIndexes[i];
      validatedItems.splice(feeIndex, 1);
      if (lineTitles.length > feeIndex) {
        lineTitles.splice(feeIndex, 1);
      }
    }
  }

  const hasInstallmentFeeLine = validatedItems.some((line) => isInstallmentFeeLine(line.id));
  if (hasInstallmentSubscription && !hasInstallmentFeeLine) {
    if (installmentConfig.variantId) {
      const feeProperties = {};
      if (installmentConfig.monthlyBaseTotal !== '') {
        feeProperties._monthly_base_total = installmentConfig.monthlyBaseTotal;
      }
      if (typeof window.getVariantGlobalIdForCart === 'function') {
        feeProperties._globalId = String(window.getVariantGlobalIdForCart(installmentConfig.variantId) || '').trim();
      }

      const feeLine = {
        id: installmentConfig.variantId,
        quantity: 1,
        properties: feeProperties
      };
      if (installmentConfig.sellingPlanId && isManualPayment) {
        feeProperties._sellingplan = String(installmentConfig.sellingPlanId);
        if (isV2Storefront()) {
          feeProperties._sub_engine_selling_plan = String(installmentConfig.sellingPlanId);
        }
      }

      if (installmentConfig.sellingPlanId && !isManualPayment) {
        feeLine.selling_plan = installmentConfig.sellingPlanId;
      }
      validatedItems.push(feeLine);
      lineTitles.push(`Variant ${installmentConfig.variantId}`);
    } else {
      // Skip silently when destination installment fee variant is unavailable in localStorage.
      silentSkips += 1;
    }
  }

  return {
    items: validatedItems,
    lineTitles: lineTitles.length > 0 ? lineTitles : null,
    warnings,
    errors,
    silentSkips,
    isSameRegion,
    isCrossMarket,
    hasImportedHsfChild
  };
};

/**
 * Cross-market shared cart: resolve each line via jp-subscription-app proxy then reconstruct properties.
 */
const validateCartItemsCrossMarket = async (sharedCartData, marketClassification, isManualPayment) => {
  const destinationMarket = String(KROWN?.settings?.current_country_code || '').trim();
  const sourceMarket = String(getSharedCartSourceMarket(sharedCartData) || '').trim().toUpperCase();
  const rawItems = sharedCartData.items || [];
  const errors = [];
  let silentSkips = 0;
  const resolvedRows = [];
  const installmentConfig = getInstallmentLocalConfig();

  const resolveCandidates = rawItems.map((item) => {
    const props = getItemProperties(item);
    if (isSourceInstallmentFeeLine(item, props)) {
      if (!installmentConfig.variantId) {
        silentSkips += 1;
        return null;
      }
      resolvedRows.push({
        sourceItem: item,
        sourceVariantId: item.variant_id,
        destinationVariantId: installmentConfig.variantId,
        quantity: item.quantity,
        sellingPlanId: installmentConfig.sellingPlanId,
        sellingPlanName: '',
        sellingPlanDescription: '',
        globalId: String(
          (typeof window.getVariantGlobalIdForCart === 'function'
            ? window.getVariantGlobalIdForCart(installmentConfig.variantId)
            : '') || props._globalId || props._globalid || ''
        ).trim(),
        variantData: {
          available: true,
          final_price: 0,
          price: 0,
          compare_at_price: null,
          inventory_quantity: 1,
          inventory_policy: 'continue',
          selling_plan_description: '',
          selling_plan_name: '',
          discount_value: '',
          monthly_base_total: installmentConfig.monthlyBaseTotal
        },
        installmentMonthlyBaseTotal: installmentConfig.monthlyBaseTotal,
        sourceLineType: 'installment_fee',
        title: item.title || item.product_title || `Variant ${item.variant_id}`
      });
      return null;
    }
    const globalId = String(props._globalId ?? props._globalid ?? '').trim();
    const fallbackTitle = item.title || item.product_title || `Variant ${item.variant_id}`;
    // Market-agnostic lines (e.g. partner application fee) have no _globalId — they
    // are resolved by handle→SKU on the backend, so let them through with global_id "".
    if (!globalId && !isSkuResolvableHandle(item.handle)) {
      errors.push({
        type: 'missing_custom_id',
        variantId: item.variant_id,
        title: fallbackTitle
      });
      return null;
    }
    return {
      item,
      globalId,
      fallbackTitle
    };
  }).filter(Boolean);

  const candidateBatches = chunkArray(resolveCandidates, SHARED_CART_RESOLVE_BATCH_LIMIT);
  for (const batch of candidateBatches) {
    const variantGlobalIds = window.KROWN?.variantGlobalIds || {};
    const batchItems = batch.map((candidate) => {
      const variantEntry = variantGlobalIds[String(candidate.item.variant_id)] || {};
      return {
        global_id: candidate.globalId,
        quantity: candidate.item.quantity,
        handle: String(candidate.item.handle || '').trim(),
        sourceMarket
      };
    });
    const resolvedBatch = await fetchSharedCartBatchResolve(batchItems, destinationMarket);

    if (!resolvedBatch.ok) {
      throw createSharedCartLoadError('Resolve batch failed');
    }

    const responseItems = Array.isArray(resolvedBatch.items) ? resolvedBatch.items : [];
    const consumedIndices = new Set();

    for (let localIndex = 0; localIndex < batch.length; localIndex += 1) {
      const candidate = batch[localIndex];
      const expectedGlobalId = candidate.globalId;
      const expectedRow = responseItems[localIndex];
      const expectedGlobalIdAtIndex = String(expectedRow?.custom_id || '').trim();

      let responseRow = null;

      if (expectedRow && expectedGlobalIdAtIndex === expectedGlobalId) {
        responseRow = expectedRow;
        consumedIndices.add(localIndex);
      } else {
        for (let i = 0; i < responseItems.length; i += 1) {
          if (consumedIndices.has(i)) continue;
          const row = responseItems[i];
          if (String(row?.custom_id || '').trim() === expectedGlobalId) {
            responseRow = row;
            consumedIndices.add(i);
            break;
          }
        }
      }

      if (!responseRow || responseRow.error) {
        errors.push({
          type: 'not_found',
          variantId: candidate.item.variant_id,
          title: candidate.fallbackTitle
        });
        continue;
      }

      const variantIdNum = parseShopifyGidToNumericId(responseRow.variantId);
      if (!variantIdNum) {
        errors.push({
          type: 'not_found',
          variantId: candidate.item.variant_id,
          title: candidate.fallbackTitle
        });
        continue;
      }

      const apiSellingPlanId = responseRow.sellingPlanId
        ? parseShopifyGidToNumericId(responseRow.sellingPlanId)
        : null;
      const destinationPlanMap = candidate.item.handle
        ? await fetchVariantSellingPlansForProductHandle(candidate.item.handle)
        : null;
      const sellingPlanNum = apiSellingPlanId
        ? resolveSellingPlanIdForVariant(destinationPlanMap, variantIdNum, apiSellingPlanId)
        : null;
      const variantData = await fetchVariantData(variantIdNum, candidate.item.handle, sellingPlanNum);

      resolvedRows.push({
        sourceItem: candidate.item,
        sourceVariantId: candidate.item.variant_id,
        destinationVariantId: variantIdNum,
        quantity: candidate.item.quantity,
        sellingPlanId: sellingPlanNum,
        sellingPlanName: String(responseRow.sellingPlanName || variantData?.selling_plan_name || '').trim(),
        sellingPlanDescription: String(variantData?.selling_plan_description || responseRow.sellingPlanName || '').trim(),
        globalId: expectedGlobalId,
        variantData,
        title: responseRow.variantTitle || candidate.fallbackTitle
      });
    }
  }

  return finalizeResolvedRows({
    resolvedRows,
    errors,
    silentSkips,
    isSameRegion: false,
    isCrossMarket: true,
    sourceMarket,
    destinationMarket: String(KROWN?.settings?.current_country_code || '').trim().toUpperCase(),
    marketClassification,
    isManualPayment
  });
};

/**
 * Validate and prepare items for cart addition with strict property reconstruction.
 */
const isSourceSubscriptionLine = (item) => {
  const props = getItemProperties(item);
  return lineHasSourceSellingPlan(item, props) || String(props._subscriptionType || '').trim() !== '';
};

const getSourcePurchaseType = (items) => {
  const lines = Array.isArray(items) ? items : [];
  const subscriptions = lines.filter(isSourceSubscriptionLine).length;
  if (subscriptions === 0) return 'onetime';
  return subscriptions === lines.length ? 'subscription' : 'mixed';
};

const resolveRestorePaymentMethod = (sharedCartData) => {
  const sourceMethod = String(sharedCartData?.attributes?._paymentMethod || '').trim();
  const api = window.JPCartPaymentMethods;
  if (!api) {
    return { sourceMethod, method: sourceMethod, availablePayments: null };
  }
  const purchaseType = getSourcePurchaseType(sharedCartData?.items);
  const method = api.resolveMethod(purchaseType, sourceMethod);
  const typeKey = purchaseType === 'onetime' ? 'one_time' : purchaseType;
  return { sourceMethod, method, availablePayments: api.rules().available[typeKey] || null };
};

const withRestorePaymentMethod = (attributes, payment) => {
  if (payment.method === payment.sourceMethod) {
    return attributes;
  }
  return {
    ...attributes,
    _paymentMethod: payment.method,
    ...(payment.availablePayments ? { _availablePayments: payment.availablePayments } : {})
  };
};

// The shared payload's attributes are the sharer's own cart attributes, so they
// carry the partner of the session that created the link (blank → Leads). Swap
// them for the share link's partner so a checkout before initPartner finishes
// is still attributed correctly; initPartner fills name/hide flag afterwards.
const SHARED_CART_PARTNER_ATTRS = [
  '_partner_id',
  '_partner_name',
  '_hide_partner_info',
  '_partner_set_in_checkout',
  '_global_partner_search'
];

const withSharePartnerAttributes = (attributes, sharedCartData) => {
  const next = { ...(attributes || {}) };
  SHARED_CART_PARTNER_ATTRS.forEach((key) => { next[key] = ''; });
  const sharePartnerId = getSharedCartSharePartnerId(sharedCartData);
  if (sharePartnerId) next._partner_id = sharePartnerId;
  return next;
};

const validateCartItems = async (sharedCartData, payment) => {
  const marketClassification = getSharedCartMarketClassification(sharedCartData);
  const isSameRegion = marketClassification.isSameRegion;
  const isManualPayment = isManualPaymentMethod(payment.method);
  if (!isSameRegion) {
    return await validateCartItemsCrossMarket(sharedCartData, marketClassification, isManualPayment);
  }

  const errors = [];
  const installmentConfig = getInstallmentLocalConfig();

  // Parallelize per-item work. Each item independently fetches variant data
  // and (for non-installment-fee items) the translated product title. Within
  // a regular item we also run the two fetches concurrently. fetchVariantData
  // and fetchTranslatedProductTitle are both handle-cached, so repeat handles
  // resolve from memory after the first network round-trip.
  const taskResults = await Promise.all(sharedCartData.items.map(async (item) => {
    const variantId = item.variant_id;
    const productHandle = item.handle;
    const sourceProps = getItemProperties(item);

    if (isSourceInstallmentFeeLine(item, sourceProps)) {
      if (!installmentConfig.variantId) {
        return { silentSkip: true };
      }
      const currentVariantData = await fetchVariantData(
        installmentConfig.variantId,
        null,
        installmentConfig.sellingPlanId
      );
      return {
        row: {
          sourceItem: item,
          sourceVariantId: variantId,
          destinationVariantId: installmentConfig.variantId,
          quantity: item.quantity,
          sellingPlanId: installmentConfig.sellingPlanId,
          sellingPlanName: String(currentVariantData?.selling_plan_name || '').trim(),
          sellingPlanDescription: String(currentVariantData?.selling_plan_description || '').trim(),
          globalId: String(
            (typeof window.getVariantGlobalIdForCart === 'function'
              ? window.getVariantGlobalIdForCart(installmentConfig.variantId)
              : '') || sourceProps._globalId || sourceProps._globalid || ''
          ).trim(),
          variantData: {
            ...(currentVariantData || {}),
            monthly_base_total: installmentConfig.monthlyBaseTotal
          },
          installmentMonthlyBaseTotal: installmentConfig.monthlyBaseTotal,
          sourceLineType: 'installment_fee',
          title: item.title || item.product_title || `Variant ${installmentConfig.variantId}`
        }
      };
    }

    const fallbackSellingPlanId =
      String(sourceProps._sellingplan || '').trim() !== ''
        ? String(sourceProps._sellingplan).trim()
        : null;
    const sellingPlanIdRaw =
      item?.selling_plan_allocation?.selling_plan?.id ||
      item?.selling_plan_id ||
      fallbackSellingPlanId;
    const storedSellingPlanId = parseShopifyGidToNumericId(sellingPlanIdRaw);
    const variantMetaMap = productHandle
      ? await fetchVariantSellingPlansForProductHandle(productHandle)
      : null;
    const sellingPlanId = lineHasSourceSellingPlan(item, sourceProps)
      ? resolveSellingPlanIdForVariant(variantMetaMap, variantId, storedSellingPlanId)
      : null;

    const [currentVariantData, translatedTitle] = await Promise.all([
      fetchVariantData(variantId, productHandle, sellingPlanId),
      productHandle ? fetchTranslatedProductTitle(productHandle, variantId) : Promise.resolve(null)
    ]);
    const productTitle = translatedTitle || item.title || item.product_title || `Variant ${variantId}`;

    return {
      row: {
        sourceItem: item,
        sourceVariantId: variantId,
        destinationVariantId: variantId,
        quantity: item.quantity,
        sellingPlanId,
        sellingPlanName: String(currentVariantData?.selling_plan_name || '').trim(),
        sellingPlanDescription: String(currentVariantData?.selling_plan_description || '').trim(),
        globalId: String(
          (typeof window.getVariantGlobalIdForCart === 'function'
            ? window.getVariantGlobalIdForCart(variantId)
            : '') || sourceProps._globalId || sourceProps._globalid || ''
        ).trim(),
        variantData: currentVariantData,
        title: productTitle
      }
    };
  }));

  const resolvedRows = [];
  let silentSkips = 0;
  for (const result of taskResults) {
    if (result && result.silentSkip) {
      silentSkips += 1;
    } else if (result && result.row) {
      resolvedRows.push(result.row);
    }
  }

  return finalizeResolvedRows({
    resolvedRows,
    errors,
    silentSkips,
    isSameRegion,
    isCrossMarket: false,
    sourceMarket: getSharedCartSourceMarket(sharedCartData),
    destinationMarket: String(KROWN?.settings?.current_country_code || '').trim().toUpperCase(),
    marketClassification,
    isManualPayment
  });
};

const addProductsToCart = async (sharedCartData, preAddPromise) => {
  try {
    const payment = resolveRestorePaymentMethod(sharedCartData);
    // Validate items before adding
    const validation = await validateCartItems(sharedCartData, payment);
    if (validation.items.length === 0) {
      if (!(sharedCartData?.items || []).length) {
        return;
      }
      if (
        (validation.silentSkips || 0) > 0 &&
        validation.errors.length === 0 &&
        validation.warnings.length === 0
      ) {
        return;
      }
      const hasMissingCustomId =
        validation.isCrossMarket &&
        validation.errors.some((e) => e && e.type === 'missing_custom_id');
      const hasResolveFailures =
        validation.isCrossMarket &&
        validation.errors.some((e) => e && e.type === 'not_found');
      let emptyKey = 'cartUpdated';
      if (validation.isCrossMarket && hasResolveFailures) {
        emptyKey = 'crossMarketLineUnavailable';
      }
      if (!validation.isCrossMarket) {
        const emptyCartVariantIds = getUniqueVariantIds([
          ...validation.errors.map((error) => error?.variantId),
          ...(sharedCartData?.items || []).map((item) => item?.variant_id)
        ]);
        const availabilityMessage = getAvailabilityMessage(emptyCartVariantIds);
        if (availabilityMessage) {
          showSharedCartNotification(buildSharedCartBannerMessage([availabilityMessage]));
          return;
        }
      } else {
        const crossMarketAvailabilityMessage = getAvailabilityMessage([
          ...validation.errors.map((error) => error?.variantId),
          ...(sharedCartData?.items || []).map((item) => item?.variant_id)
        ]);
        if (crossMarketAvailabilityMessage) {
          showSharedCartNotification(buildSharedCartBannerMessage([crossMarketAvailabilityMessage]));
          return;
        }
      }
      if (emptyKey === 'crossMarketLineUnavailable') {
        const crossMarketUnavailableMessage = getAvailabilityMessage([
          ...validation.errors.map((error) => error?.variantId),
          ...(sharedCartData?.items || []).map((item) => item?.variant_id)
        ]);
        showSharedCartNotification(buildSharedCartBannerMessage([
          crossMarketUnavailableMessage || t(emptyKey)
        ]));
      } else {
        showSharedCartNotification(buildSharedCartBannerMessage());
      }
      return;
    }
    
    const cartAttributes = withRestorePaymentMethod(
      withSharePartnerAttributes(
        sanitizeSharedCartHsfAttributes(sharedCartData.attributes, validation.hasImportedHsfChild),
        sharedCartData
      ),
      payment
    );
    
    // Store payment method type
    sessionStorage.setItem("paymentMethodType", cartAttributes._paymentMethod);
    
    // Resolve titles for each requested item up front, using only what's
    // already in validation.lineTitles / sharedCartData — no extra network
    // calls per item.
    const resolveItemTitle = (item, index) => {
      if (validation.lineTitles && validation.lineTitles[index]) {
        return validation.lineTitles[index];
      }
      if (!validation.isCrossMarket) {
        const sharedItem = sharedCartData.items.find((shared) => shared.variant_id === item.id);
        if (sharedItem) {
          return sharedItem.title || sharedItem.product_title || `Variant ${item.id}`;
        }
      }
      return `Variant ${item.id}`;
    };

    // Single batched add. Shopify's /cart/add.js accepts an items[] array and
    // adds them in one round-trip; replaces the old N x (cart.js + add.js +
    // 100ms sleep) loop. Partial-inventory handling falls back to a per-item
    // retry path only when this batch returns 422.
    const itemsAddedSuccessfully = [];
    const itemsFailed = [];

    // Wait for any caller-supplied pre-condition (e.g. /cart/clear.js fired in
    // parallel with the API call + validation pass) before mutating the cart.
    // Required because cart/clear and cart/add are order-sensitive at Shopify.
    if (preAddPromise) {
      await preAddPromise.catch(() => null);
    }

    // gate:false — this add IS the shared-cart restore, so it must not wait on
    // the restore lock it is responsible for releasing.
    const batchResponse = await (window.JPCartService
      ? window.JPCartService.enqueuePost('/cart/add.js', JSON.stringify({ items: validation.items, attributes: cartAttributes }), { 'Content-Type': 'application/json' }, { gate: false })
      : fetchWithTimeout('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: validation.items,
            attributes: cartAttributes
          })
        })).catch(() => null);

    if (!batchResponse) {
      throw createSharedCartLoadError('Cart add batch request failed');
    }
    if (!batchResponse.ok && !isRecoverableCartAddFailure(batchResponse)) {
      throw createSharedCartLoadError('Cart add batch returned non-recoverable status');
    }

    if (batchResponse.ok) {
      // 200: all items added at full quantity. Response is { items: [...] }
      // with the line items that were just added.
      validation.items.forEach((item, index) => {
        itemsAddedSuccessfully.push({
          item,
          title: resolveItemTitle(item, index),
          data: null
        });
      });
    } else {
      // 422 or transport failure: isolate invalid lines by retrying per-item.
      // This prevents one draft/unavailable product from blocking valid items.
      let errorMessage = t('cartUpdated');
      if (batchResponse) {
        const errorData = await batchResponse.json().catch(() => ({}));
        errorMessage = errorData.description || errorData.message || errorMessage;
      }

      for (let i = 0; i < validation.items.length; i++) {
        const item = validation.items[i];
        const title = resolveItemTitle(item, i);
        
        const singleAddResponse = await (window.JPCartService
          ? window.JPCartService.enqueuePost('/cart/add.js', JSON.stringify({ items: [item], attributes: cartAttributes }), { 'Content-Type': 'application/json' }, { gate: false })
          : fetchWithTimeout('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                items: [item],
                attributes: cartAttributes
              })
            })).catch(() => null);

        if (!singleAddResponse) {
          throw createSharedCartLoadError('Cart add single-line request failed');
        }
        if (!singleAddResponse.ok && !isRecoverableCartAddFailure(singleAddResponse)) {
          throw createSharedCartLoadError('Cart add single-line returned non-recoverable status');
        }

        if (!singleAddResponse.ok) {
          let lineErrorMessage = errorMessage;
          if (singleAddResponse) {
            const lineErrorData = await singleAddResponse.json().catch(() => ({}));
            lineErrorMessage = lineErrorData.description || lineErrorData.message || lineErrorMessage;
          }
          itemsFailed.push({
            item,
            title,
            error: lineErrorMessage,
            reason: lineErrorMessage
          });
        } else {
          const addedData = await singleAddResponse.json().catch(() => null);
          const addedLine = Array.isArray(addedData?.items) ? addedData.items[0] : null;
          const addedQuantity = Number(addedLine?.quantity || item.quantity);

          if (addedQuantity < item.quantity) {
            itemsAddedSuccessfully.push({
              item: { ...item, quantity: addedQuantity },
              title,
              data: addedData,
              partial: true,
              requestedQuantity: item.quantity,
              addedQuantity,
              missingQuantity: item.quantity - addedQuantity
            });
            continue;
          }

          itemsAddedSuccessfully.push({
            item,
            title,
            data: addedData
          });
        }
      }
    }
    
    
    
    // If no items were added successfully, show notification and return
    if (itemsAddedSuccessfully.length === 0) {
      if (validation.items.length > 0 && validation.errors.length === 0) {
        throw createSharedCartLoadError('Cart add failed');
      }
      if (!validation.isCrossMarket) {
        const unavailableVariantIds = getUniqueVariantIds([
          ...validation.errors.map((error) => error?.variantId),
          ...itemsFailed.map((failed) => failed?.item?.id),
          ...(sharedCartData?.items || []).map((item) => item?.variant_id)
        ]);
        const availabilityMessage = getAvailabilityMessage(unavailableVariantIds);
        if (availabilityMessage) {
          showSharedCartNotification(buildSharedCartBannerMessage([availabilityMessage]));
          return;
        }
      }
      if (validation.isCrossMarket) {
        const crossMarketUnavailableMessage = getAvailabilityMessage([
          ...validation.errors.map((error) => error?.variantId),
          ...itemsFailed.map((failed) => failed?.item?.id),
          ...(sharedCartData?.items || []).map((item) => item?.variant_id)
        ]);
        showSharedCartNotification(buildSharedCartBannerMessage([
          crossMarketUnavailableMessage || t('crossMarketLineUnavailable')
        ]));
      } else {
        showSharedCartNotification(buildSharedCartBannerMessage());
      }
      return;
    }
    
    // Get the final cart state
    const cartResponse = await fetchWithTimeout('/cart.js').catch(() => null);
    if (!cartResponse || !cartResponse.ok) {
      throw createSharedCartLoadError('Cart state fetch failed after add');
    }
    const data = await cartResponse.json();
    
    
    
    // Compare requested items with actually added items in cart
    const itemsNotAdded = [];
    const itemsPartiallyAdded = [];
    
    
    // Add failed items to itemsNotAdded
    itemsFailed.forEach(failed => {
      itemsNotAdded.push({
        variantId: failed.item.id,
        title: failed.title,
        quantity: failed.item.quantity,
        reason: failed.reason || failed.error || t('cartUpdated')
      });
    });
    
    // Check for partial quantity adds from successful adds that were marked as partial
    itemsAddedSuccessfully.forEach(successItem => {
      if (successItem.partial) {
        itemsPartiallyAdded.push({
          variantId: successItem.item.id,
          title: successItem.title,
          requestedQuantity: successItem.requestedQuantity,
          addedQuantity: successItem.addedQuantity,
          missingQuantity: successItem.missingQuantity,
          reason: t('cartUpdated')
        });
      }
    });
    
    
    
    // Also check cart state for any discrepancies (double-check)
    for (let reqIdx = 0; reqIdx < validation.items.length; reqIdx++) {
      const requestedItem = validation.items[reqIdx];
      // Skip if this item already failed or was marked as partial
      if (itemsFailed.some(f => f.item.id === requestedItem.id)) {
        continue;
      }
      if (itemsPartiallyAdded.some(p => p.variantId === requestedItem.id)) {
        continue;
      }

      const sharedItem = validation.isCrossMarket
        ? null
        : sharedCartData.items.find((line) => line.variant_id === requestedItem.id);
      // Title sourced from validation.lineTitles (already populated upstream)
      // and shared cart payload — no per-item network call needed here.
      let itemTitle =
        validation.lineTitles && validation.lineTitles[reqIdx]
          ? validation.lineTitles[reqIdx]
          : `Variant ${requestedItem.id}`;
      if (!validation.isCrossMarket && sharedItem && !validation.lineTitles?.[reqIdx]) {
        itemTitle = sharedItem.title || sharedItem.product_title || itemTitle;
      }
      
      // Check if this item exists in the cart response
      const addedItem = findMatchingAddedCartLine(data.items, requestedItem);
      
      if (!addedItem) {
        // Item was not added at all (shouldn't happen if individual add succeeded, but check anyway)
        itemsNotAdded.push({
          variantId: requestedItem.id,
          title: itemTitle,
          quantity: requestedItem.quantity,
          reason: t('cartUpdated')
        });
      } else if (addedItem.quantity < requestedItem.quantity) {
        // Partial quantity added (catch any we might have missed)
        const alreadyTracked = itemsPartiallyAdded.some(p => p.variantId === requestedItem.id);
        if (!alreadyTracked) {
          itemsPartiallyAdded.push({
            variantId: requestedItem.id,
            title: itemTitle,
            requestedQuantity: requestedItem.quantity,
            addedQuantity: addedItem.quantity,
            missingQuantity: requestedItem.quantity - addedItem.quantity,
            reason: t('cartUpdated')
          });
        }
      }
    }
    
    const priceWarnings = [];
    if (!validation.isCrossMarket) {
      for (let reqIdx = 0; reqIdx < validation.items.length; reqIdx++) {
        const requestedItem = validation.items[reqIdx];
        const sourceItem = sharedCartData.items.find((line) => line.variant_id === requestedItem.id);
        if (!sourceItem) continue;

        const sourceProps = getItemProperties(sourceItem);
        const lineType = detectLineType(sourceProps, requestedItem.id);
        if (lineType === 'hsf_child') continue;

        const addedItem = findMatchingAddedCartLine(data.items, requestedItem);
        if (!addedItem) continue;

        const sharedPrice = getEffectiveLineFinalPrice(sourceItem);
        const currentPrice = getEffectiveLineFinalPrice(addedItem);
        if (sharedPrice == null || currentPrice == null || sharedPrice === currentPrice) continue;

        const warningTitle =
          validation.lineTitles && validation.lineTitles[reqIdx]
            ? validation.lineTitles[reqIdx]
            : (sourceItem.title || sourceItem.product_title || `Variant ${requestedItem.id}`);
        priceWarnings.push({
          variantId: requestedItem.id,
          title: warningTitle,
          sharedPrice,
          currentPrice
        });
      }
    }

    const availabilityIssueVariantIds = getUniqueVariantIds([
      ...validation.errors.map((error) => error?.variantId),
      ...itemsNotAdded.map((item) => item?.variantId),
      ...itemsPartiallyAdded.map((item) => item?.variantId)
    ]);
    window.sharedCartAvailabilityIssueVariantIds = availabilityIssueVariantIds;
    window.sharedCartPriceChangeVariantIds = getUniqueVariantIds(
      priceWarnings.map((warning) => warning?.variantId)
    );

    const hasMissingCustomId =
      validation.isCrossMarket &&
      validation.errors.some((e) => e && e.type === 'missing_custom_id');
    const hasCrossMarketResolveFailures =
      validation.isCrossMarket &&
      validation.errors.some((e) => e && e.type === 'not_found');

    const hasChanges =
      validation.errors.length > 0 ||
      itemsNotAdded.length > 0 ||
      itemsPartiallyAdded.length > 0 ||
      priceWarnings.length > 0;

    window.sharedCartAvailabilityIssueVariantIds = getUniqueVariantIds([
      ...validation.errors.map((error) => error?.variantId),
      ...itemsNotAdded.map((item) => item?.variantId),
      ...itemsPartiallyAdded.map((item) => item?.variantId)
    ]);
    window.sharedCartPriceChangeVariantIds = getUniqueVariantIds(
      validation.warnings.map((warning) => warning?.variantId)
    );

    if (hasChanges) {
      const detailMessages = [];
      if (!validation.isCrossMarket) {
        const warningTitles = [
          ...getUniqueNotificationTitles(
            priceWarnings.map((warning) => warning?.title)
          )
        ];
        if (warningTitles.length > 0) {
          const priceMessageKey = getSameRegionMessageKey('sameRegionPriceUpdated', warningTitles.length);
          detailMessages.push(
            t(priceMessageKey, {
              products: warningTitles.join(', ')
            })
          );
        }

        const availabilityMessage = getAvailabilityMessage([...availabilityIssueVariantIds]);
        if (availabilityMessage !== '') {
          detailMessages.push(availabilityMessage);
        }
      } else {
        const crossMarketAvailabilityMessage = getAvailabilityMessage([...availabilityIssueVariantIds]);
        if (crossMarketAvailabilityMessage !== '') {
          detailMessages.push(crossMarketAvailabilityMessage);
        } else if (hasCrossMarketResolveFailures) {
          detailMessages.push(t('crossMarketLineUnavailable'));
        }
      }
      showSharedCartNotification(buildSharedCartBannerMessage(detailMessages));
    } 

    
  } catch (error) {
    throw error;
  }
};

(async () => {
  try {
    if (typeof window.syncCartPageLoaderVariant === 'function') {
      window.syncCartPageLoaderVariant();
    }
    if (loader2) loader2.classList.remove("hidden");

    const cartToken = checkCartTokenInUrl();
    const storedCartToken = sessionStorage.getItem('storedCartToken');

    if (cartToken && cartToken !== storedCartToken) {
      sessionStorage.setItem('storedCartToken', cartToken);

      // Fire /cart/clear.js in parallel with the API call + validation pass.
      // retrieveCart awaits this promise via addProductsToCart's preAddPromise
      // argument just before /cart/add.js, preserving the required order
      // (clear must complete before add) without serializing the wall-clock.
      // The cart service's queue keeps that order, and sharedCartRestore (armed
      // in theme.liquid) holds every OTHER writer off until we release below.
      // gate:false because this clear is the restore — waiting on the lock it
      // releases would deadlock.
      const clearPromise = (window.JPCartService
        ? window.JPCartService.clear({ gate: false })
        : fetchWithTimeout('/cart/clear.js', { method: 'POST' })
            .then((response) => response && response.json && response.json()))
        .catch(() => null);

      const cartContent = await retrieveCart(clearPromise);

      const sharePartnerId = getSharedCartSharePartnerId(cartContent);
      announceSharedCartSharePartnerId(sharePartnerId);

      if (typeof window.releaseSharedCartRestore === 'function') {
        window.releaseSharedCartRestore();
      }

      maybeShowCartSourceMarketRedirectModal(cartContent);

      if (window.SharedCartTracking && typeof window.SharedCartTracking.trackLoadedSharedCart === 'function') {
        window.SharedCartTracking.trackLoadedSharedCart({
          cartToken,
          sharedCartData: cartContent,
          unavailableVariantIds: getUniqueVariantIds(window.sharedCartAvailabilityIssueVariantIds || []),
          priceChangeVariantIds: getUniqueVariantIds(window.sharedCartPriceChangeVariantIds || [])
        });
      }

      await refreshCart();

      if (typeof window.refreshCartPaymentMethods === "function") {
        await window.refreshCartPaymentMethods();
      }

      showSharedCartPartnerMessageForId(sharePartnerId);
    } else if (cartToken) {
      const rememberedSharePartnerId = getRememberedSharedCartSharePartnerId();
      announceSharedCartSharePartnerId(rememberedSharePartnerId);
      showSharedCartPartnerMessageForId(rememberedSharePartnerId);
    }
  } catch (error) {
    if (error && error.code === 'CART_TOKEN_EXPIRED') {
      showSharedCartTokenExpiredBanner();
    } else if (error && error.code === SHARED_CART_LOAD_ERROR) {
      showSharedCartLoadErrorBanner();
    } else {
      console.log("error in custom cart api", error);
    }
  } finally {
    // Always release: skip-restore, market redirect return, or error mid-add.
    if (typeof window.releaseSharedCartRestore === 'function') {
      window.releaseSharedCartRestore();
    }
    if (loader2) loader2.classList.add('hidden');
    $('#AjaxCartSubtotal').css('display', 'block');
    document.dispatchEvent(new Event('customCartLoaded'));
  }
})();

